import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';
import socketService from '../services/socketService';
import api from '../services/api';
import userService from '../services/userService';
import { formatTime } from '../utils/format';
import { AuthContext } from './AuthContext';

const NotificationContext = createContext(null);

const toNumberOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeRouteTarget = (target, fallbackState = {}) => {
  if (!target || typeof target !== 'object') return null;

  if (target.path && typeof target.path === 'string') {
    return {
      path: target.path,
      state: target.state || fallbackState,
      entityType: target.entityType || null,
      entityId: toNumberOrNull(target.entityId),
      anchor: target.anchor || null
    };
  }

  if (target.route && typeof target.route === 'string') {
    return {
      path: target.route,
      state: target.state || fallbackState,
      entityType: target.entity_type || target.entityType || null,
      entityId: toNumberOrNull(target.entity_id || target.entityId),
      anchor: target.anchor || null
    };
  }

  return null;
};

const deriveTargetFromNotification = (notification) => {
  const explicitRoute = normalizeRouteTarget(
    notification.target || notification.route_target || notification.routeTarget,
    {
      sourceNotificationId: notification.id,
      sourceNotificationEvent: notification.event || null
    }
  );

  if (explicitRoute) {
    return explicitRoute;
  }

  const event = notification.event || '';
  const ticketId = toNumberOrNull(notification.ticket_id || notification.ticketId || notification.entity_id);
  const projectId = toNumberOrNull(notification.project_id || notification.projectId);
  const taskId = toNumberOrNull(notification.task_id || notification.taskId);
  const userId = toNumberOrNull(notification.user_id || notification.userId);
  const branchId = toNumberOrNull(notification.branch_id || notification.branchId);

  if (ticketId || event.startsWith('ticket:') || event.startsWith('comment:')) {
    if (!ticketId) return null;
    return {
      path: `/tickets/${ticketId}`,
      state: {
        sourceNotificationId: notification.id,
        sourceNotificationEvent: event,
        scrollTo: event.startsWith('comment:') ? 'comments' : null
      },
      entityType: 'ticket',
      entityId: ticketId,
      anchor: event.startsWith('comment:') ? 'comments' : null
    };
  }

  if (taskId || event.startsWith('task:')) {
    return {
      path: '/projects',
      state: {
        sourceNotificationId: notification.id,
        sourceNotificationEvent: event,
        focusProjectId: projectId,
        focusTaskId: taskId
      },
      entityType: 'task',
      entityId: taskId || null,
      anchor: null
    };
  }

  if (projectId || event.startsWith('project:')) {
    return {
      path: '/projects',
      state: {
        sourceNotificationId: notification.id,
        sourceNotificationEvent: event,
        focusProjectId: projectId
      },
      entityType: 'project',
      entityId: projectId || null,
      anchor: null
    };
  }

  if (branchId || event.startsWith('branch:')) {
    return {
      path: '/branches',
      state: {
        sourceNotificationId: notification.id,
        sourceNotificationEvent: event,
        focusBranchId: branchId
      },
      entityType: 'branch',
      entityId: branchId || null,
      anchor: null
    };
  }

  if (userId || event.startsWith('user:')) {
    return {
      path: '/users',
      state: {
        sourceNotificationId: notification.id,
        sourceNotificationEvent: event,
        focusUserId: userId
      },
      entityType: 'user',
      entityId: userId || null,
      anchor: null
    };
  }

  return null;
};

const normalizeNotificationPayload = (payload, fallbackType = 'info', fallbackTitle = 'إشعار') => {
  const normalized = typeof payload === 'string'
    ? { message: payload, type: fallbackType, title: fallbackTitle }
    : (payload || {});

  const timestamp = normalized.timestamp ? new Date(normalized.timestamp) : new Date();
  const target = deriveTargetFromNotification(normalized);

  return {
    id: normalized.id || Date.now(),
    message: normalized.message || '',
    type: normalized.type || fallbackType,
    title: normalized.title || fallbackTitle,
    event: normalized.event || null,
    actor_name: normalized.actor_name || null,
    ticket_id: toNumberOrNull(normalized.ticket_id || normalized.ticketId),
    ticket_number: normalized.ticket_number || null,
    entity_type: normalized.entity_type || target?.entityType || null,
    entity_id: toNumberOrNull(normalized.entity_id || target?.entityId),
    target,
    timestamp,
    read: Boolean(normalized.read)
  };
};

const endpointByEntity = {
  ticket: (id) => `/tickets/${id}`,
  project: (id) => `/projects/${id}`,
  branch: (id) => `/branches/${id}`,
  user: (id) => `/users/${id}`
};

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState({
    total: 0,
    byRole: { admin: 0, supervisor: 0, technician: 0, branch: 0, other: 0 },
    users: []
  });
  const [showToast, setShowToast] = useState(false);
  const [currentToast, setCurrentToast] = useState(null);

  // Add a notification
  const addNotification = useCallback((payload, type = 'info', title = 'إشعار') => {
    const notification = normalizeNotificationPayload(payload, type, title);

    setNotifications((prev) => [notification, ...prev].slice(0, 100));
    if (!notification.read) {
      setUnreadCount((prev) => prev + 1);
    }

    // Show toast
    setCurrentToast(notification);
    setShowToast(true);
  }, []);

  // Clear a specific notification
  const clearNotification = useCallback((id) => {
    setNotifications((prev) => {
      const target = prev.find((n) => n.id === id);
      if (target && !target.read) {
        setUnreadCount((count) => Math.max(0, count - 1));
      }
      return prev.filter((n) => n.id !== id);
    });
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const markAsRead = useCallback((id) => {
    setNotifications((prev) => {
      let changed = false;

      const next = prev.map((n) => {
        if (n.id !== id || n.read) return n;
        changed = true;
        return { ...n, read: true };
      });

      if (changed) {
        setUnreadCount((count) => Math.max(0, count - 1));
      }

      return next;
    });
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => (n.read ? n : { ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const resolveNotificationTarget = useCallback((notification) => {
    if (!notification) return null;
    return notification.target || deriveTargetFromNotification(notification);
  }, []);

  const validateNotificationTarget = useCallback(async (target) => {
    if (!target || !target.path) {
      return { ok: false, message: 'لا يحتوي هذا الإشعار على وجهة صالحة.' };
    }

    const endpointFactory = endpointByEntity[target.entityType];
    if (!endpointFactory || !target.entityId) {
      return { ok: true };
    }

    try {
      await api.get(endpointFactory(target.entityId));
      return { ok: true };
    } catch (error) {
      const status = error?.response?.status;

      if (status === 404) {
        return { ok: false, message: 'العنصر المرتبط بهذا الإشعار لم يعد موجوداً.' };
      }

      if (status === 401 || status === 403) {
        return { ok: false, message: 'ليس لديك صلاحية للوصول إلى هذا العنصر.' };
      }

      return { ok: true };
    }
  }, []);

  // Setup socket listeners
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && user) {
      userService.getOnlineUsers()
        .then((response) => setOnlineUsers(response))
        .catch(() => {});

      // Connect to socket
      socketService.connect();

      // Listen for ticket created
      socketService.onTicketCreated((ticket) => {
        addNotification({
          message: `تم إنشاء تذكرة جديدة: ${ticket.ticket_number}`,
          type: 'info',
          title: 'تذكرة جديدة',
          ticket_id: ticket.id,
          ticket_number: ticket.ticket_number,
          entity_type: 'ticket',
          entity_id: ticket.id,
          target: {
            path: `/tickets/${ticket.id}`,
            entityType: 'ticket',
            entityId: ticket.id
          },
          event: 'ticket:created',
          timestamp: new Date().toISOString()
        });
      });

      // Listen for ticket updated
      socketService.onTicketUpdated((ticket) => {
        addNotification({
          message: `تم تحديث التذكرة: ${ticket.ticket_number}`,
          type: 'info',
          title: 'تحديث تذكرة',
          ticket_id: ticket.id,
          ticket_number: ticket.ticket_number,
          entity_type: 'ticket',
          entity_id: ticket.id,
          target: {
            path: `/tickets/${ticket.id}`,
            entityType: 'ticket',
            entityId: ticket.id
          },
          event: 'ticket:updated',
          timestamp: new Date().toISOString()
        });
      });

      // Listen for ticket assigned
      socketService.onTicketAssigned((ticket) => {
        addNotification({
          message: `تم تكليفك بتذكرة: ${ticket.ticket_number}`,
          type: 'success',
          title: 'تكليف جديد',
          ticket_id: ticket.id,
          ticket_number: ticket.ticket_number,
          entity_type: 'ticket',
          entity_id: ticket.id,
          target: {
            path: `/tickets/${ticket.id}`,
            entityType: 'ticket',
            entityId: ticket.id
          },
          event: 'ticket:assigned',
          timestamp: new Date().toISOString()
        });
      });

      // Listen for comments
      socketService.onCommentAdded(({ ticketId, comment }) => {
        addNotification({
          message: `تم إضافة تعليق جديد على التذكرة #${ticketId}`,
          type: 'info',
          title: 'تعليق',
          actor_name: comment?.user_name || null,
          ticket_id: ticketId,
          entity_type: 'ticket',
          entity_id: ticketId,
          target: {
            path: `/tickets/${ticketId}`,
            entityType: 'ticket',
            entityId: ticketId,
            anchor: 'comments'
          },
          event: 'comment:added',
          timestamp: new Date().toISOString()
        });
      });

      // Listen for general notifications
      socketService.onNotification((notification) => {
        addNotification(notification);
      });

      socketService.onConnectedUsers((presence) => {
        setOnlineUsers(presence);
      });
    }

    // Cleanup on unmount
    return () => {
      socketService.off('ticket:created');
      socketService.off('ticket:updated');
      socketService.off('ticket:assigned');
      socketService.off('comment:added');
      socketService.off('notification');
      socketService.off('connected_users');
      socketService.disconnect();
    };
  }, [addNotification, user]);

  const contextValue = useMemo(() => ({
    notifications,
    unreadCount,
    addNotification,
    clearNotification,
    clearAll,
    markAsRead,
    markAllAsRead,
    resolveNotificationTarget,
    validateNotificationTarget,
    onlineUsers
  }), [notifications, unreadCount, addNotification, clearNotification, clearAll, markAsRead, markAllAsRead, resolveNotificationTarget, validateNotificationTarget, onlineUsers]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      
      {/* Toast notification */}
      <ToastContainer position="top-end" className="p-3">
        <Toast 
          show={showToast} 
          onClose={() => setShowToast(false)} 
          delay={5000} 
          autohide
          bg={
            currentToast?.type === 'success' ? 'success' :
            currentToast?.type === 'error' ? 'danger' :
            currentToast?.type === 'warning' ? 'warning' : 'info'
          }
        >
          <Toast.Header>
            <strong className="me-auto">{currentToast?.title}</strong>
            <small className="text-muted">{formatTime(currentToast?.timestamp)}</small>
          </Toast.Header>
          <Toast.Body className="text-white">
            {currentToast?.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
